import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { blogArticles } from '@/mocks/seo-content';

const blogHeroImages: Record<string, string> = {
  'pandabuyspreadsheet-guide': 'https://readdy.ai/api/search-image?query=Clean%20modern%20spreadsheet%20interface%20displayed%20on%20laptop%20and%20tablet%20side%20by%20side%2C%20warm%20minimalist%20workspace%20with%20cream%20walls%20and%20natural%20light%2C%20editorial%20productivity%20photography%2C%20professional%20yet%20inviting%20aesthetic%2C%20soft%20shadows%2C%20elegant%20composition%2C%20high%20detail%20screen%20content%20visible&width=1200&height=600&seq=blog-hero-guide-v3&orientation=landscape',
  'best-sneaker-spreadsheets-2026': 'https://readdy.ai/api/search-image?query=Premium%20sneaker%20collection%20displayed%20on%20minimalist%20white%20shelving%2C%20warm%20ambient%20lighting%2C%20clean%20editorial%20product%20photography%2C%20varied%20colorways%20arranged%20artistically%2C%20soft%20shadows%2C%20modern%20retail%20aesthetic&width=1200&height=600&seq=blog-hero-sneaker-v3&orientation=landscape',
  'streetwear-comparison-guide': 'https://readdy.ai/api/search-image?query=Curated%20streetwear%20outfit%20flat%20lay%20with%20hoodie%20jacket%20and%20accessories%20on%20warm%20concrete%20surface%2C%20editorial%20fashion%20photography%2C%20soft%20natural%20lighting%2C%20urban%20minimalist%20aesthetic%2C%20clean%20composition&width=1200&height=600&seq=blog-hero-street-v3&orientation=landscape',
  'pandabuyspreadsheet-for-resellers': 'https://readdy.ai/api/search-image?query=Organized%20product%20inventory%20on%20minimalist%20shelving%20with%20spreadsheet%20on%20laptop%20screen%20nearby%2C%20warm%20workspace%20lighting%2C%20clean%20editorial%20photography%2C%20professional%20reseller%20setup%2C%20modern%20aesthetic&width=1200&height=600&seq=blog-hero-resell-v3&orientation=landscape',
  'top-rated-spreadsheet-lists': 'https://readdy.ai/api/search-image?query=Five%20star%20rating%20display%20with%20spreadsheet%20grid%20patterns%20in%20background%2C%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20editorial%20design%2C%20clean%20geometric%20composition%2C%20modern%20SaaS%20aesthetic%2C%20soft%20lighting&width=1200&height=600&seq=blog-hero-rated-v3&orientation=landscape',
  'pandabuyspreadsheet-mobile-guide': 'https://readdy.ai/api/search-image?query=Smartphone%20displaying%20clean%20spreadsheet%20interface%20on%20screen%2C%20held%20in%20hand%20against%20warm%20minimalist%20cafe%20background%2C%20soft%20natural%20light%2C%20editorial%20mobile%20productivity%20photography%2C%20modern%20lifestyle%20aesthetic&width=1200&height=600&seq=blog-hero-mobile-v3&orientation=landscape',
  'pandabuyspreadsheet-europe-guide': 'https://readdy.ai/api/search-image?query=Elegant%20European%20cityscape%20with%20minimalist%20shopping%20aesthetic%2C%20warm%20continental%20architecture%20in%20soft%20light%2C%20clean%20editorial%20travel%20photography%2C%20sophisticated%20lifestyle%20scene%2C%20neutral%20warm%20tones&width=1200&height=600&seq=blog-hero-europe-v3&orientation=landscape',
  'compare-products-fast': 'https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20visualization%20with%20organized%20product%20data%20columns%2C%20warm%20minimalist%20digital%20interface%20design%2C%20clean%20geometric%20layout%2C%20modern%20productivity%20aesthetic%2C%20soft%20ambient%20lighting%2C%20abstract%20data%20patterns&width=1200&height=600&seq=blog-hero-fast-v3&orientation=landscape',
  'pandabuyspreadsheet-weekly-updates': 'https://readdy.ai/api/search-image?query=Calendar%20and%20spreadsheet%20interface%20with%20fresh%20product%20listings%20highlighted%2C%20warm%20minimalist%20desk%20setup%2C%20soft%20natural%20lighting%2C%20clean%20editorial%20productivity%20photography%2C%20organized%20workspace%20aesthetic&width=1200&height=600&seq=blog-hero-weekly-v3&orientation=landscape',
  'beginner-vs-advanced-spreadsheets': 'https://readdy.ai/api/search-image?query=Progressive%20learning%20visualization%20with%20basic%20to%20advanced%20spreadsheet%20interfaces%2C%20warm%20minimalist%20design%2C%20clean%20step%20by%20step%20layout%2C%20modern%20educational%20aesthetic%2C%20soft%20ambient%20lighting&width=1200&height=600&seq=blog-hero-advanced-v3&orientation=landscape',
};

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">Article Not Found</h1>
          <Link to="/" className="mt-4 inline-block text-accent-600 hover:text-accent-700 cursor-pointer">Return to Homepage</Link>
        </div>
        <Footer />
      </>
    );
  }

  const heroImage = blogHeroImages[article.slug] || blogHeroImages['pandabuyspreadsheet-guide'];

  return (
    <>
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 px-4 md:px-6">
        <nav className="max-w-3xl mx-auto flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
            Home
          </Link>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-xs"></i>
          </span>
          <span className="text-foreground-900 font-medium truncate">{article.title}</span>
        </nav>
      </div>

      <main>
        {/* Article Header */}
        <article className="pt-6 pb-16">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            {/* Category Tag */}
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-semibold uppercase tracking-wider mb-4">
              {article.category}
            </span>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm"></i>
                </span>
                {article.publishedDate}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-sm"></i>
                </span>
                {article.author}
              </span>
            </div>

            {/* Featured Image */}
            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                src={heroImage}
                alt={article.title}
                title={article.title}
                className="w-full h-auto"
                loading="eager"
              />
            </div>

            {/* Schema.org Article Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: article.title,
                description: article.sections[0]?.body?.substring(0, 160) || article.title,
                datePublished: article.publishedDate,
                dateModified: article.publishedDate,
                author: {
                  '@type': 'Organization',
                  name: article.author,
                },
                publisher: {
                  '@type': 'Organization',
                  name: 'Pandabuy.digital',
                  logo: {
                    '@type': 'ImageObject',
                    url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
                  },
                },
                image: heroImage,
                url: `https://pandabuy.digital/blog/${article.slug}`,
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `https://pandabuy.digital/blog/${article.slug}`,
                },
              })}
            </script>

            {/* Article Body */}
            <div className="mt-10 space-y-10">
              {article.sections.map((section, idx) => {
                if (section.isFaq) {
                  return (
                    <section key={idx}>
                      {/* Schema.org FAQPage Structured Data */}
                      <script type="application/ld+json">
                        {JSON.stringify({
                          '@context': 'https://schema.org',
                          '@type': 'FAQPage',
                          mainEntity: section.faqs?.map((faq) => ({
                            '@type': 'Question',
                            name: faq.question,
                            acceptedAnswer: {
                              '@type': 'Answer',
                              text: faq.answer,
                            },
                          })),
                        })}
                      </script>
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                        {section.heading}
                      </h2>
                      <div className="space-y-4">
                        {section.faqs?.map((faq, fi) => (
                          <details key={fi} className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                            <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                              <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</span>
                              <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 group-open:rotate-45 transition-transform">
                                <i className="ri-add-line text-lg"></i>
                              </span>
                            </summary>
                            <div className="px-5 pb-4">
                              <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                            </div>
                          </details>
                        ))}
                      </div>
                    </section>
                  );
                }

                return (
                  <section key={idx}>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                      {section.heading}
                    </h2>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      {section.body}
                    </p>
                  </section>
                );
              })}
            </div>

            {/* Internal CTA Block */}
            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 text-center">
              <h3 className="text-lg font-heading font-bold text-foreground-900">
                Ready to Put This Knowledge Into Practice?
              </h3>
              <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto">
                Explore all pandabuyspreadsheet categories and start comparing products with the structured shopping system today.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  Back to Pandabuyspreadsheet Home
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </span>
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-foreground-100 transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  Visit Main Platform
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-10">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">More Pandabuyspreadsheet Guides</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {blogArticles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/blog/${related.slug}`}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 border border-background-200/60 hover:border-background-300/80 text-sm text-foreground-700 hover:text-foreground-900 transition-all duration-200 cursor-pointer group"
                    >
                      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-accent-500">
                        <i className="ri-article-line text-sm"></i>
                      </span>
                      <span className="line-clamp-1 group-hover:text-foreground-900 transition-colors">{related.title}</span>
                    </Link>
                  ))}
              </div>
            </div>

            {/* Back link */}
            <div className="mt-8 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 font-medium transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-xs"></i>
                </span>
                Return to Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}